import { QUERY_NAME, ROUTES, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { ResponseHistory } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { AxiosError } from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Course, FilterResponse } from "types/elearningCourse.type";

const truncateValue = (val: any) => {
  const str = String(val);
  return str.length > 255 ? str.substring(0, 255) : str;
};

export const useGetCourseDetail = (id: string) => {
  const companyId = getCompanyId();
  const navigate = useNavigate();
  const isValidId = !!id && !isNaN(Number(id));

  const query = useQuery(
    [QUERY_NAME.LESSON_LIST, companyId, id],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.E_LEARNING_COURSE}/${id}`,
        params: { company_id: companyId },
      }).then((res) => ({ data: res.data as Course })),
    {
      enabled: isValidId,
      cacheTime: 300,
      staleTime: 300,
    },
  );

  // xử lý lỗi ở bên ngoài — đảm bảo chỉ chạy 1 lần
  useEffect(() => {
    if (query.isError && query.error instanceof AxiosError) {
      if (query.error.response?.status === 404) {
        notification.error({ message: "コースは存在しません。" });
        navigate(`/${ROUTES.Electronic_Learning}`);
      }
    }
  }, [query.isError]);

  return query;
};

export const useGetCourseList = (
  page: number,
  pageSize: number,
  filters: any,
) => {
  const companyId = getCompanyId();
  const { __BLOCK_API__, ...restFilters } = filters || {};
  return useQuery(
    [QUERY_NAME.E_LEARNING_COURSE_LIST, companyId, page, pageSize, restFilters],
    () =>
      axios({
        method: "get",
        url: SERVICES.E_LEARNING_COURSE,
        params: {
          ...(pageSize !== 0 && {
            page,
            per_page: pageSize,
          }),
          company_id: companyId,
          ...restFilters,
        },
      }).then((res) => ({
        data: res.data as Course[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      enabled: !__BLOCK_API__,
      cacheTime: 100,
      staleTime: 100,
    },
  );
};

export const useCreateOrUpdateCourseList = (id?: string) => {
  const isUpdate = !!id;
  const companyId = getCompanyId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const url = isUpdate
    ? SERVICES.E_LEARNING_COURSE + "/" + id
    : SERVICES.E_LEARNING_COURSE;
  return useMutation(
    (data: any) =>
      axios({
        method: isUpdate ? "put" : "post",
        url,
        params: {
          company_id: companyId,
        },
        data: data,
      }),
    {
      onSuccess(res) {
        queryClient.invalidateQueries([QUERY_NAME.E_LEARNING_COURSE_LIST]);
        if (res.data?.status === "published") {
          queryClient.invalidateQueries([
            QUERY_NAME.E_LEARNING_COURSE_ON_STORE_LIST,
          ]);
        }
        queryClient.invalidateQueries([QUERY_NAME.LESSON_LIST]);

        notification["success"]({
          message: "保存しました",
        });
        navigate(`/${ROUTES.Electronic_Learning}?tab=list`);
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useGetCourseOnStoreList = (
  page: number,
  pageSize: number,
  filters: any,
) => {
  const companyId = getCompanyId();
  const { __BLOCK_API__, ...restFilters } = filters || {};
  return useQuery(
    [
      QUERY_NAME.E_LEARNING_COURSE_ON_STORE_LIST,
      companyId,
      page,
      pageSize,
      restFilters,
    ],
    () =>
      axios({
        method: "get",
        url: SERVICES.E_LEARNING_COURSE_ON_STORE,
        params: {
          ...(pageSize !== 0 && {
            page,
            per_page: pageSize,
          }),

          company_id: companyId,
          ...restFilters,
        },
      }).then((res) => ({
        data: res.data as Course[],
        meta: {
          total: Number(res.headers["x-total"]) || 0,
          total_page: Number(res.headers["x-total-pages"]) || 0,
        },
      })),
    {
      enabled: !__BLOCK_API__,
      cacheTime: 300,
      staleTime: 300,
    },
  );
};

export const useUpdatePublish = (id: string) => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: any) =>
      axios({
        method: "post",
        url: `${SERVICES.E_LEARNING_COURSE}/${id}${SERVICES.PUBLIC}`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.E_LEARNING_COURSE_ON_STORE_LIST,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.LESSON_LIST]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useUpdateCourseDetail = (
  id?: string | number,
  onSubmitSuccess?: () => void,
) => {
  const isUpdate = !!id;
  const companyId = getCompanyId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const url = isUpdate
    ? SERVICES.E_LEARNING_COURSE + "/" + id
    : SERVICES.E_LEARNING_COURSE;
  return useMutation(
    (data: any) =>
      axios({
        method: isUpdate ? "put" : "post",
        url,
        params: {
          company_id: companyId,
        },
        data: data,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.E_LEARNING_COURSE_LIST]);
        queryClient.invalidateQueries([QUERY_NAME.LESSON_LIST]);
        queryClient.invalidateQueries([QUERY_NAME.LESSON_DETAIL]);

        notification["success"]({
          message: "保存しました",
        });
        onSubmitSuccess && onSubmitSuccess();
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useSubscribeCourse = () => {
  const companyId = getCompanyId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (data: any) =>
      axios({
        method: "post",
        url: SERVICES.E_LEARNING_COURSE + "/" + data.id + SERVICES.BUY,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess(res) {
        queryClient.invalidateQueries([
          QUERY_NAME.E_LEARNING_COURSE_ON_STORE_LIST,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.E_LEARNING_COURSE_LIST]);
        queryClient.invalidateQueries([
          QUERY_NAME.LESSON_LIST,
          companyId,
          res.data.course_id,
        ]);

        notification["success"]({
          message: "保存しました",
        });
        navigate(`/${ROUTES.Electronic_Learning}`);
      },
      onError(error: any) {
        const errorMessage = error?.response?.data?.error;
        if (errorMessage) {
          notification["error"]({
            message: errorMessage,
          });
        } else {
          notification["error"]({
            message: "保存に失敗しました。",
          });
        }
      },
    },
  );
};

export const useGetCourseHistories = ({
  courseId,
  page,
  pageSize,
  workerId,
  filterParams,
  enabled,
}: {
  courseId: string;
  page: number;
  pageSize: number;
  workerId?: number;
  filterParams: any;
  enabled?: boolean;
}) => {
  const companyId = getCompanyId();

  return useQuery(
    [
      QUERY_NAME.COURSE_HISTORIES,
      companyId,
      courseId,
      page,
      pageSize,
      workerId,
      filterParams,
    ],
    () =>
      axios<ResponseHistory[]>({
        method: "get",
        url: SERVICES.E_LEARNING_COURSE + `/${courseId}` + SERVICES.HISTORIES,
        params: {
          ...(pageSize !== 0 && {
            page,
            per_page: pageSize,
          }),
          company_id: companyId,
          ...(workerId && {
            worker_id: workerId,
          }),
          ...filterParams,
        },
      }),
    {
      cacheTime: 300,
      staleTime: 300,
      enabled,
    },
  );
};

export const useGetWatchedWorkers = (courseId: string) => {
  const companyId = getCompanyId();

  return useQuery(
    [QUERY_NAME.COURSE_VIEWERS, companyId, courseId],
    () =>
      axios({
        method: "get",
        url: SERVICES.E_LEARNING_COURSE + `/${courseId}` + SERVICES.VIEWERS,
        params: {
          company_id: companyId,
        },
      }),
    {
      cacheTime: 300,
      staleTime: 300,
    },
  );
};

export const useDeleteMultipleCourse = (onDeleteSuccess?: () => void) => {
  const company_id = getCompanyId();

  return useMutation(
    (course_ids: number[]) =>
      axios({
        method: "post",
        url: SERVICES.E_LEARNING_COURSE + "/bulk_delete",
        data: { course_ids, company_id },
      }),
    {
      onSuccess(data) {
        const errors = data.data?.errors;
        if (errors?.length) {
          errors.forEach((error: any) => {
            notification.error(error);
          });
        } else {
          notification["success"]({
            message: "保存しました",
          });
          onDeleteSuccess && onDeleteSuccess();
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useRestoreCourse = () => {
  const company_id = getCompanyId();

  return useMutation(
    (course_id: number) =>
      axios({
        method: "post",
        url: SERVICES.E_LEARNING_COURSE + `/${course_id}` + "/restore",
        data: { company_id, id: course_id },
      }),
    {
      onSuccess(data) {
        const errors = data.data?.errors;
        if (errors?.length) {
          errors.forEach((error: any) => {
            notification.error(error);
          });
        } else {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(e) {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useGetCourseFilterHistories = ({
  courseId,
  workerId,
  enabled,
  params,
}: {
  courseId: string;
  workerId?: number;
  params: any;
  enabled: boolean;
}) => {
  const companyId = getCompanyId();

  return useQuery(
    [
      QUERY_NAME.HISTORY_FILTER_OPTIONS,
      companyId,
      courseId,
      workerId,
      JSON.stringify(params),
    ],
    () => {
      return axios({
        method: "get",
        url: SERVICES.E_LEARNING_COURSE + `/${courseId}/history_filter_options`,
        params: {
          company_id: companyId,
          ...(workerId && {
            worker_id: workerId,
          }),
          ...params,
        },
      }).then((data) => data.data as FilterResponse);
    },
    {
      cacheTime: 300,
      staleTime: 300,
      enabled,
    },
  );
};

export const useGetCourseFilterOptions = (
  payload: { field_names: string[]; active_filters?: any } | null,
  enabled: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    ["E_LEARNING_COURSE_FILTER_OPTIONS", companyId, payload],
    () =>
      axios({
        method: "post",
        url: SERVICES.E_LEARNING_COURSE + "/filter_options",
        data: {
          company_id: companyId,
          active_filters: payload?.active_filters,
          field_names: payload?.field_names ?? [
            "title",
            "description",
            "created_at",
            "status",
            "lesson_count",
          ],
        },
      }).then((res) => res.data),
    {
      enabled,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useGetCourseOnStoreFilterOptions = (
  payload: { field_names: string[]; active_filters?: any } | null,
  enabled: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    ["E_LEARNING_COURSE_ON_STORE_FILTER_OPTIONS", companyId, payload],
    () =>
      axios({
        method: "post",
        url: SERVICES.E_LEARNING_COURSE_ON_STORE + "/filter_options",
        data: {
          company_id: companyId,
          active_filters: payload?.active_filters,
          field_names: payload?.field_names ?? [
            "title",
            "description",
            "created_at",
            "lesson_count",
          ],
        },
      }).then((res) => res.data),
    {
      enabled,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};
